import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { RenderUnsettledUI } from 'shared/components';
import { ContractorCompanyRate } from 'api/services/company/company-service.schema';
import { formatRateDisplay } from './contractor-rate-values';
import { useGetContractorCompanyRates } from './hooks/use-get-contractor-company-rates';
import { useContractorRateColumns } from './use-contractor-rate-columns';

const CompanyDetailContractorRates = () => {
  const query = useGetContractorCompanyRates();

  if (query.status !== 'success') {
    return <RenderUnsettledUI data={query} />;
  }

  return <CompanyDetailContractorRatesTable jobTypes={query.data.jobTypes} />;
};

const CompanyDetailContractorRatesTable = ({ jobTypes }: { jobTypes: ContractorCompanyRate[] }) => {
  const { t } = useTranslation('ogp-screen-specific');
  const { columns, jobTypeLabel, rateUnitLabel } = useContractorRateColumns();

  return (
    <TableContainer role="tabpanel">
      {jobTypes.length === 0 ? (
        <Typography>{t('company.contractor-rates.empty.description')}</Typography>
      ) : (
        <RatesTable data-testid="contractor-company-rates">
          <TableHead>
            <TableRow>
              <TableCell>{jobTypeLabel}</TableCell>
              {columns.map((column) => (
                <TableCell key={column.employmentType}>
                  {column.label}
                  <RateUnit variant="caption" color="text.secondary">
                    {rateUnitLabel}
                  </RateUnit>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {jobTypes.map((jobType) => (
              <TableRow key={jobType.jobTypeId}>
                <TableCell>{jobType.jobTypeName}</TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column.employmentType}
                    data-testid={`contractor-rate-${jobType.jobTypeId}-${column.employmentType}`}
                  >
                    {formatRateDisplay(column.rateCents(jobType))}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </RatesTable>
      )}
    </TableContainer>
  );
};

const RatesTable = styled(Table)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const RateUnit = styled(Typography)({
  display: 'block',
});

export { CompanyDetailContractorRates };
